import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import applyConverters from 'axios-case-converter'
import Qs from 'qs'
import { setLocalIsAuthenticated } from '../lib/local_storage'
import { i18n } from '../components/i18n'

const CSRF_HEADER = 'X-CSRF-TOKEN'

let csrfToken: string | undefined
let csrfSeedPromise: Promise<void> | undefined

const readCsrfTokenFromHeaders = (headers: Record<string, unknown> | undefined): string | undefined => {
  if (!headers) return

  const raw = headers['x-csrf-token'] ?? headers['X-CSRF-TOKEN'] ?? headers.xCsrfToken ?? headers[CSRF_HEADER]
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined
}

const rememberCsrfToken = (headers: Record<string, unknown> | undefined): void => {
  const token = readCsrfTokenFromHeaders(headers)
  if (token) csrfToken = token
}

const isMutatingMethod = (method?: string): boolean => {
  const verb = (method ?? 'get').toLowerCase()
  return verb !== 'get' && verb !== 'head' && verb !== 'options'
}

const applyCsrfTokenToRequest = (config: AxiosRequestConfig): AxiosRequestConfig => {
  if (!csrfToken || !isMutatingMethod(config.method)) return config

  config.headers = {
    ...config.headers,
    [CSRF_HEADER]: csrfToken,
  }
  return config
}

const _config = {
  headers: {
    'x-requested-with': 'XMLHttpRequest',
    accept: 'application/json',
    'accept-language': i18n.language,
  },
  withCredentials: true,
  paramsSerializer: (params: any) => Qs.stringify(params, { arrayFormat: 'brackets' }),
}

const axiosInstance = axios.create(_config)
applyConverters(axiosInstance, { preservedKeys: ['_destroy'], ignoreHeaders: true })

// TODO: Switch endpoints by env
if (process.env.REACT_APP_RAILS_ENV === 'development' || !process.env.REACT_APP_API_PREFIX || !process.env.REACT_APP_API_SUFFIX) {
  axiosInstance.defaults.baseURL = process.env.REACT_APP_API_BASE_URL
} else {
  const hostname = window.location.hostname.replace('integration.', '')
  axiosInstance.defaults.baseURL = process.env.REACT_APP_API_PREFIX + hostname + process.env.REACT_APP_API_SUFFIX
}

axiosInstance.defaults.xsrfCookieName = `CSRF-TOKEN_${String(process.env.REACT_APP_RAILS_ENV)}`
axiosInstance.defaults.xsrfHeaderName = CSRF_HEADER
axiosInstance.defaults.withCredentials = true

axiosInstance.interceptors.request.use(
  async request => {
    if (isMutatingMethod(request.method) && !csrfToken && csrfSeedPromise) {
      await csrfSeedPromise
    }
    return applyCsrfTokenToRequest(request)
  },
  async error => await Promise.reject(error)
)

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    rememberCsrfToken(response.headers as Record<string, unknown>)
    return response
  },
  async (error) => {
    rememberCsrfToken(error?.response?.headers as Record<string, unknown> | undefined)
    if (error?.response?.status === 401) {
      setLocalIsAuthenticated(false)
      if (error?.config.url !== '/sessions') {
        alert('ログインしてください')
        location.href = '/login'
      }
    }
    return await Promise.reject(error)
  }
)

const seedCsrfToken = (): Promise<void> => {
  csrfSeedPromise = (async () => {
    try {
      await axiosInstance.get('/health_check')
    } catch {
      // Token may still have been stored from error response headers.
    }
  })()
  return csrfSeedPromise
}

const resetCsrfTokenForTests = (): void => {
  csrfToken = undefined
  csrfSeedPromise = undefined
}

if (process.env.NODE_ENV !== 'test') {
  csrfSeedPromise = seedCsrfToken()
}

export { axiosInstance, readCsrfTokenFromHeaders, applyCsrfTokenToRequest, isMutatingMethod, seedCsrfToken, resetCsrfTokenForTests, CSRF_HEADER }
